import {
  Box,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TableCell,
  TableRow,
  Tooltip,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  RESELLER_MEMBER_STATUS_HINTS,
  RESELLER_MEMBER_STATUS_LABELS,
  assignableRoles,
  resellerMemberNeedsAttention,
  roleLabel,
  summarizeOverrides,
  type ResellerMember,
  type ResellerMemberStatus,
  type ResellerRoleKey,
  type ResellerRoleMatrix,
} from "@xop/dashboard-shared";
import { AlertTriangle, MailPlus, MoreHorizontal, Settings2, Trash2, UserMinus } from "lucide-react";
import { useState } from "react";
import type { JSX } from "react";

import { rowSelectSx } from "@/pages/admin/dashboard-users/tableStyles";

/** Chip colour per verdict. `orphaned_membership` and `account_disabled` are
 * warnings rather than errors on purpose: neither grants access, so nothing is
 * on fire — but both are half-states a human has to finish. */
const STATUS_COLOR: Record<ResellerMemberStatus, "success" | "info" | "warning" | "default"> = {
  active: "success",
  invited: "info",
  account_disabled: "warning",
  orphaned_membership: "warning",
  revoked: "default",
};

function formatWhen(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

export function ResellerMemberRow({
  member,
  matrix,
  canManage,
  onRoleChange,
  onPermissions,
  onResendInvite,
  onRevoke,
  onDelete,
}: {
  member: ResellerMember;
  /** The company's role matrix — supplies its custom roles and their labels.
   * Absent while loading/failed: the select then offers the built-ins plus
   * the member's own current role, labelled from `role_label`. */
  matrix?: ResellerRoleMatrix | undefined;
  canManage: boolean;
  onRoleChange: (role: ResellerRoleKey) => void;
  onPermissions: () => void;
  onResendInvite: () => void;
  onRevoke: () => void;
  /** "Delete user" — removes the person entirely. Offered on a revoked row
   * too: that is the usual way to clear one off the roster. */
  onDelete: () => void;
}): JSX.Element {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const closeMenu = (): void => setMenuAnchor(null);

  const { membership, account, status } = member;
  const revoked = !membership.active;
  const displayName = account?.full_name?.trim() || membership.email;
  // "Role · +granted · −denied" — the bare role label when there is no
  // override. Read-only/revoked rows render this AS the label so overrides
  // stay visible even without the editable select; the line below the label
  // is then redundant when the two are identical, so it is skipped in that
  // case rather than repeating the same text twice.
  const summary = summarizeOverrides(membership, matrix);
  const bareLabel = roleLabel(membership.role, matrix, membership.role_label);
  // Built-ins + this company's custom roles. The member's CURRENT role is
  // always an option, even when the matrix does not (yet) list it — a select
  // whose value matches no option would silently show the first one.
  const options = assignableRoles(matrix);
  if (!options.some((o) => o.key === membership.role)) {
    options.push({ key: membership.role, label: bareLabel });
  }

  return (
    <TableRow
      data-cy="reseller-member-row"
      data-email={membership.email}
      data-status={status}
      // A revoked row stays visible but recedes: "never invited" and "access
      // removed" are different facts, and hiding the second makes them
      // indistinguishable.
      {...(revoked ? { sx: { opacity: 0.55 } } : {})}
    >
      <TableCell>
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ fontWeight: 500, color: "text.primary" }}>{displayName}</Box>
          {displayName !== membership.email && (
            <Box sx={{ fontSize: 12, color: "text.disabled" }}>{membership.email}</Box>
          )}
        </Box>
      </TableCell>

      <TableCell>
        {canManage && !revoked ? (
          <Box
            component="select"
            value={membership.role}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              onRoleChange(e.target.value as ResellerRoleKey)
            }
            aria-label={`Reseller role for ${membership.email}`}
            data-cy="reseller-member-role-select"
            sx={rowSelectSx}
          >
            {options.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </Box>
        ) : (
          <Box sx={{ fontSize: 13, color: "text.secondary" }}>{summary}</Box>
        )}
        {(canManage && !revoked) || summary !== bareLabel ? (
          <Box data-cy="reseller-member-summary" data-testid="reseller-member-summary" sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
            {summary}
          </Box>
        ) : null}
      </TableCell>

      <TableCell>
        <Tooltip title={RESELLER_MEMBER_STATUS_HINTS[status]}>
          <Chip
            size="small"
            label={RESELLER_MEMBER_STATUS_LABELS[status]}
            color={STATUS_COLOR[status]}
            variant={status === "revoked" ? "outlined" : "filled"}
            {...(resellerMemberNeedsAttention(status)
              ? { icon: <AlertTriangle size={13} /> }
              : {})}
            sx={{ fontSize: 11, height: 22 }}
          />
        </Tooltip>
      </TableCell>

      <TableCell sx={{ fontSize: 13, color: "text.secondary" }}>
        {formatWhen(account?.last_sign_in_at ?? null)}
      </TableCell>

      <TableCell sx={{ textAlign: "right" }}>
        {canManage && (
          <>
            <IconButton
              size="small"
              aria-label={`Actions for ${membership.email}`}
              data-cy="reseller-member-actions"
              onClick={(e) => setMenuAnchor(e.currentTarget)}
            >
              <MoreHorizontal size={16} />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={closeMenu}>
              {membership.role !== "owner" && (
                <MenuItem
                  disabled={revoked}
                  data-cy="reseller-member-permissions"
                  onClick={() => {
                    closeMenu();
                    onPermissions();
                  }}
                >
                  <ListItemIcon>
                    <Settings2 size={16} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Permissions…"
                    secondary="Grant or deny on top of the role"
                    secondaryTypographyProps={{ sx: { fontSize: 11 } }}
                  />
                </MenuItem>
              )}
              <MenuItem
                disabled={revoked}
                data-cy="reseller-member-resend"
                onClick={() => {
                  closeMenu();
                  onResendInvite();
                }}
              >
                <ListItemIcon>
                  <MailPlus size={16} />
                </ListItemIcon>
                <ListItemText
                  primary="Re-send invite"
                  // The same call repairs an orphaned membership: portal-api's
                  // create-with-invite is idempotent by email, so it both makes
                  // a missing account and re-mails an existing one.
                  secondary={
                    status === "orphaned_membership"
                      ? "Creates the missing sign-in account"
                      : "Re-sends the set-password link"
                  }
                  secondaryTypographyProps={{ sx: { fontSize: 11 } }}
                />
              </MenuItem>
              <MenuItem
                disabled={revoked}
                data-cy="reseller-member-revoke"
                onClick={() => {
                  closeMenu();
                  onRevoke();
                }}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon sx={{ color: "error.main" }}>
                  <UserMinus size={16} />
                </ListItemIcon>
                <ListItemText
                  primary="Revoke access"
                  secondary="Deactivates the CRM binding and the sign-in"
                  secondaryTypographyProps={{ sx: { fontSize: 11 } }}
                />
              </MenuItem>
              <MenuItem
                data-cy="reseller-member-delete"
                onClick={() => {
                  closeMenu();
                  onDelete();
                }}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon sx={{ color: "error.main" }}>
                  <Trash2 size={16} />
                </ListItemIcon>
                <ListItemText
                  primary="Delete user"
                  secondary="Removes them from this company and deletes the sign-in"
                  secondaryTypographyProps={{ sx: { fontSize: 11 } }}
                />
              </MenuItem>
            </Menu>
          </>
        )}
      </TableCell>
    </TableRow>
  );
}
